import { clientOnlyCheck } from '../utils/client-only';

/** The dashboard's own production host. */
const PRODUCTION_HOST = 'app.sparklayer.io';

/** The production host the dashboard is served from when embedded in the Shopify admin. */
const EMBEDDED_PRODUCTION_HOST = 'shop.ignite.sparklayer.io';

/** The shared development host — branch builds are served from sub-paths of it. */
export const DEVELOPMENT_HOST = 'app.dev.sparklayer.io';

/**
 * Whether `host` serves production merchants, embedded or not. Anything host-keyed that must match
 * a real merchant's environment (app slugs, OAuth client IDs) has to go through this, otherwise the
 * embedded host silently falls through to development credentials.
 */
export function isProductionHost(host: string): boolean {
  return host === PRODUCTION_HOST || host === EMBEDDED_PRODUCTION_HOST;
}

export const isProduction = clientOnlyCheck(() => isProductionHost(window.location.host));

export const shopifyAppSlug = clientOnlyCheck(() => isProductionHost(window.location.host))
  ? 'sparklayer'
  : 'sparklayer-dev';

/** The SparkLayer Wix app's ID, used to deep-link into its page in the Wix dashboard. */
export const wixAppId = clientOnlyCheck(() => isProductionHost(window.location.host))
  ? '50df5afe-a808-4d2e-95dc-8c181d506cf3'
  : 'd6e8fbb7-6f92-4d1a-881b-bca9df6f94be';

/** The hosts the dashboard is served from locally. */
const LOCAL_HOSTS = ['app.local.sparklayer.io', 'localhost'];

/**
 * Every host the dashboard itself is served from. The embedded host is deliberately absent: pages
 * restricted to these run on the dashboard only, not inside a platform's admin.
 */
export const DASHBOARD_HOSTS = [PRODUCTION_HOST, DEVELOPMENT_HOST, ...LOCAL_HOSTS];

/** Whether `host` is the dashboard itself rather than a platform integration. */
export function isDashboardHost(host: string): boolean {
  return DASHBOARD_HOSTS.includes(host);
}
